import React from 'react';
import { Box } from '@mui/material';
import type { Transaction } from '../../../services/transactionApi';
import { BudgetHero } from './BudgetHero';
import { PendingBillsCard } from './PendingBillsCard';
import { GoalsProgressCard } from './GoalsProgressCard';
import { CategoryBudgetBars } from './CategoryBudgetBars';
import { RhythmCard } from './RhythmCard';
import { PersonalRecent } from './RecentTransactions';
import type { MonthSummary, PendingBill, Goal, CategorySpend, MonthlyExpense, CumulativeSeries } from './dashboard.types';

export interface DashboardHomeProps {
  /** the month in focus, YYYY-MM */
  month: string;
  summary: MonthSummary;
  /** expense entries this month, for the "Despesas" hint */
  expenseEntries: number;
  /** income entries this month, for the "Receitas" hint */
  incomeEntries: number;
  /** expenses vs the previous month, as a fraction */
  expensesDelta?: number;
  bills: PendingBill[];
  goals: Goal[];
  categories: CategorySpend[];
  /** 6 months, the last one being the month in focus */
  months: MonthlyExpense[];
  cumulative: CumulativeSeries;
  /** the month's latest entries, newest first */
  recent: Transaction[];
  currency: string;
  /** the "Entre grupos" card, which loads on its own */
  groupsCard?: React.ReactNode;
  onSeeBills?: () => void;
  onSeeGoals?: () => void;
  onSeeTransactions?: () => void;
  onSetBudget?: () => void;
}

const row = (min: number) =>
  ({ display: 'grid', gridTemplateColumns: `repeat(auto-fit, minmax(min(${min}px, 100%), 1fr))`, gap: { xs: 2, md: 2.5 }, alignItems: 'stretch' }) as const;

/**
 * The personal Início: the budget on top, then what is due, the goals and
 * the groups; where the money went and its rhythm; the latest entries.
 */
export function DashboardHome(props: DashboardHomeProps) {
  const { currency } = props;

  return (
    <Box sx={{ display: 'grid', gap: { xs: 2, md: 2.5 } }}>
      <BudgetHero
        month={props.month}
        summary={props.summary}
        incomeEntries={props.incomeEntries}
        expenseEntries={props.expenseEntries}
        expensesDelta={props.expensesDelta}
        projectedEnd={props.cumulative.projectedEnd}
        currency={currency}
        onSetBudget={props.onSetBudget}
      />

      <Box sx={row(300)}>
        <PendingBillsCard bills={props.bills} onSeeAll={props.onSeeBills} />
        <GoalsProgressCard goals={props.goals} onSeeAll={props.onSeeGoals} />
        {/* on a phone, what the groups owe comes right after the budget */}
        {props.groupsCard && <Box sx={{ display: 'flex', flexDirection: 'column', order: { xs: -1, md: 0 }, '& > *': { flex: 1 } }}>{props.groupsCard}</Box>}
      </Box>

      <Box sx={row(420)}>
        <CategoryBudgetBars categories={props.categories} currency={currency} />
        <Box sx={{ display: { xs: 'none', md: 'flex' }, flexDirection: 'column', '& > *': { flex: 1 } }}>
          <RhythmCard cumulative={props.cumulative} months={props.months} currency={currency} />
        </Box>
      </Box>

      <PersonalRecent transactions={props.recent} onSeeAll={props.onSeeTransactions ?? (() => undefined)} />
    </Box>
  );
}
